import type { AxiosInstance } from 'axios';
import merge from 'lodash/merge';

import { Ajax } from '@newrelic/browser-agent/features/ajax';
import { GenericEvents } from '@newrelic/browser-agent/features/generic_events';
import { JSErrors } from '@newrelic/browser-agent/features/jserrors';
import { Logging } from '@newrelic/browser-agent/features/logging';
import { Metrics } from '@newrelic/browser-agent/features/metrics';
import { PageViewEvent } from '@newrelic/browser-agent/features/page_view_event';
import { PageViewTiming } from '@newrelic/browser-agent/features/page_view_timing';
import { SessionReplay } from '@newrelic/browser-agent/features/session_replay';
import { SessionTrace } from '@newrelic/browser-agent/features/session_trace';
import { Spa } from '@newrelic/browser-agent/features/spa';
import { Agent } from '@newrelic/browser-agent/loaders/agent';
import type { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

import type { CreateNewRelicBrowserAgentParams } from './model';

export const createNewRelicBrowserAgent = ({
  accountID,
  applicationID,
  licenseKey,
  agentOptions,
}: CreateNewRelicBrowserAgentParams) => {
  const options = merge(
    {
      loader_config: {
        accountID: accountID,
        trustKey: accountID,
        agentID: applicationID,
        licenseKey: licenseKey,
        applicationID: applicationID,
      },
      init: {
        session_replay: {
          enabled: true,
          block_selector: '',
          mask_text_selector: '*',
          sampling_rate: 0.05,
          error_sampling_rate: 100.0,
          mask_all_inputs: true,
          collect_fonts: true,
          inline_images: false,
          inline_stylesheet: true,
          fix_stylesheets: true,
          mask_input_options: {},
        },
        distributed_tracing: {
          enabled: true,
        },
        privacy: {
          cookies_enabled: true,
        },
        ajax: {
          deny_list: ['bam.eu01.nr-data.net'],
        },
      },
      info: {
        beacon: 'bam.eu01.nr-data.net',
        errorBeacon: 'bam.eu01.nr-data.net',
        licenseKey: licenseKey,
        applicationID: applicationID,
        sa: 1,
      },
      features: [
        // Enable all available NR features
        // https://github.com/newrelic/newrelic-browser-agent?tab=readme-ov-file#composing-a-custom-agent-with-selected-feature-modules
        Ajax,
        GenericEvents,
        JSErrors,
        Logging,
        PageViewEvent,
        PageViewTiming,
        SessionReplay,
        SessionTrace,
        Spa,
        Metrics,
      ],
    },
    agentOptions
  );

  const newAgent = new Agent(options);
  configureConsoleLogger(newAgent);
  configureErrorHandler(newAgent);
  return newAgent;
};

const configureConsoleLogger = (agent: BrowserAgent) => {
  // https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/wraplogger/#wrap-examples
  agent.wrapLogger(console, 'debug', {
    level: 'DEBUG',
  });
  agent.wrapLogger(console, 'trace', {
    level: 'TRACE',
  });
  agent.wrapLogger(console, 'log', {
    level: 'INFO',
  });
  agent.wrapLogger(console, 'info', {
    level: 'INFO',
  });
  agent.wrapLogger(console, 'warn', {
    level: 'WARN',
  });
  agent.wrapLogger(console, 'error', {
    level: 'ERROR',
  });
};

const configureErrorHandler = (agent: BrowserAgent) => {
  agent.setErrorHandler((_) => {
    // https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/seterrorhandler/#examples
    // If the handler returns true, New Relic does not record the error. Otherwise the error will be processed normally.
    return false;
  });
};

export const configureAxiosResponseTiming = (
  agent: BrowserAgent,
  axios: AxiosInstance,
  appVersion?: string,
  environment?: string,
  dcCode?: string
) => {
  axios.interceptors.response.use((originalResponse) => {
    const config = originalResponse.config as any;
    const endTime = performance.now();
    const serverTimeHeader = originalResponse.headers['server-elapsedtime'];

    const responseTime = Math.round(endTime - config.metadata.startTime);
    const serverTime = serverTimeHeader
      ? Number(serverTimeHeader)
      : responseTime;

    if (agent !== null && agent !== undefined) {
      const customAttributes = {
        responseTime: responseTime,
        serverTime: serverTime,
        url: originalResponse.config.url,
        appVersion: appVersion,
        env: environment,
        dc: dcCode,
      };

      agent.log('Response time', {
        customAttributes: customAttributes,
      });
    }

    return originalResponse;
  });
};
